import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  INTL_MARKETS,
  useConditionalFeature,
} from '@americanexpress/conditional-features';
import { IconChange } from '@americanexpress/dls-icons';
import { IconButton } from '@americanexpress/dls-react';
import { RenderModule } from 'holocron';
import {
  getStartLocation,
  getEndLocation,
  getIsLocationsSwapDisabled,
} from '../../selectors/requestSelectors';
import { getIsErrorByField } from '../../selectors/validationSelectors';
import {
  setStartLocationAction,
  setEndLocationAction,
  swapLocationsAction,
} from '../../ducks/requestDuck';
import styles from './PackageLocations.scss';
import themeContext from '../../context/themeContext';
import { DIRECTIONS } from '../../constants/flightConstants';

const PackageLocations = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const useSetStartLocation = () => (value) => dispatch(
    setStartLocationAction(value));

  const useSetEndLocation = () => (value) => dispatch(
    setEndLocationAction(value));

  const useSwapLocation = () => (value) => dispatch(
    swapLocationsAction(value));

  const onStartLocationChange = useSetStartLocation();
  const onEndLocationChange = useSetEndLocation();
  const onSwapLocations = useSwapLocation();
  const startLocation = useSelector(getStartLocation);
  const endLocation = useSelector(getEndLocation);
  const isLocationSwapDisabled = useSelector(getIsLocationsSwapDisabled);
  const isStartLocationWarning = useSelector(getIsErrorByField('startLocation'));
  const isEndLocationWarning = useSelector(getIsErrorByField('endLocation'));
  const hasTimeDropdown = useConditionalFeature(INTL_MARKETS);

  const commonPackageLocationProps = {
    version: 2,
    theme: useContext(themeContext),
    type: 'PACKAGE',
  };

  const locationsClass = clsx('col-xs-12 pad-1-b-sm-down pad-0-r-md-up flex', {
    'col-md-6 col-xl-7': hasTimeDropdown,
    'col-md-7 col-xl-8': !hasTimeDropdown,
  });

  return (
    <div className={locationsClass} data-testid="packageLocations">
      <div className={styles.locationsBox} data-testid="startLocation">
        <RenderModule
          moduleName="axp-travel-search-locations"
          props={{
            hasWarning: isStartLocationWarning,
            value: startLocation,
            closeButtonAriaLabel: formatMessage({
              id: 'from.closeButton.ariaLabel',
            }),
            placeholder: formatMessage({ id: 'from' }),
            onChange: onStartLocationChange,
            direction: DIRECTIONS.DEPARTURE,
            ...commonPackageLocationProps,
          }}
        />
      </div>
      <div className="flex flex-align-end pad-1-lr">
        <IconButton
          id="packageLocationsSwapButton"
          className="btn-icon pad-0"
          type="button"
          aria-disabled={isLocationSwapDisabled}
          onClick={onSwapLocations}
          screenReaderLabel={formatMessage({ id: 'swapButton.ariaLabel' })}
          variant="primary"
        >
          <IconChange
            className={clsx('pad-0')}
            size="md"
          />
        </IconButton>
      </div>
      <div className={styles.locationsBox} data-testid="endLocation">
        <RenderModule
          moduleName="axp-travel-search-locations"
          props={{
            hasWarning: isEndLocationWarning,
            value: endLocation,
            closeButtonAriaLabel: formatMessage({
              id: 'from.closeButton.ariaLabel',
            }),
            placeholder: formatMessage({ id: 'to' }),
            onChange: onEndLocationChange,
            direction: DIRECTIONS.DESTINATION,
            ...commonPackageLocationProps,
          }}
        />
      </div>
    </div>
  );
};

export default PackageLocations;
